import React, { useRef, useState } from 'react';
import {
  Badge, Box, Button, Flex, HStack, Text, useColorModeValue, useToast,
} from '@chakra-ui/react';
import { FiDownload, FiPaperclip, FiTrash2 } from 'react-icons/fi';
import {
  StoredFileScope, useStoredFiles, useStoredFileActions, downloadStoredFile,
} from '../hooks/useStoredFiles';
import StoredFilePreviewModal, { PreviewFile } from './StoredFilePreviewModal';

// item 257 — upload a RECEIVED purchase order (the client's authorization) and attach it to
// the doc it belongs to (Proposal / Work Order / Invoice / the approval wizard). Rides the
// StoredFile mechanism (scope=ReceivedPurchaseOrder, RefId = the parent doc id; item 792
// resolves the whole deal chain, so a PO uploaded at approval shows on the WO + invoice too).
//
// item 948 (Chad) — this "just didn't work" with zero feedback:
//  1. The Upload button had no type="button" — inside the invoice/WO <form> the browser
//     treated it as SUBMIT, so clicking it submitted the form instead of opening the picker.
//  2. Failures were swallowed (unawaited promise) — now every outcome toasts, with the
//     server's actual reason on failure.
//  3. Files open in the in-app preview modal on click (item 935); download is its own icon.
//
// item 1574 (Chad: "sometimes we have another document, signed by the customer for hardware
// delivery") — the same box serves a second slot, kind="delivery" (scope=SignedDelivery). It
// rides the same deal chain and is attached to the invoice email beside the client's PO.
export type DealAttachmentKind = 'po' | 'delivery';

const KIND: Record<DealAttachmentKind, {
  scope: StoredFileScope; title: string; blurb: string; noun: string;
}> = {
  po: {
    scope: StoredFileScope.ReceivedPurchaseOrder,
    title: 'Received PO',
    blurb: 'The client\u2019s purchase order authorizing this job. Click a file to preview it.',
    noun: 'PO',
  },
  delivery: {
    scope: StoredFileScope.SignedDelivery,
    title: 'Signed delivery',
    blurb: 'The delivery document the customer signed for the hardware. It goes out with the invoice.',
    noun: 'delivery document',
  },
};

const ReceivedPoAttachments: React.FC<{ docId: string; kind?: DealAttachmentKind; mt?: number }> = ({ docId, kind = 'po', mt = 3 }) => {
  const k = KIND[kind];
  const { data: files } = useStoredFiles(k.scope, docId);
  const { upload, remove, isUploading } = useStoredFileActions(k.scope, docId);
  const fileRef = useRef<HTMLInputElement>(null);
  const toast = useToast();
  const [preview, setPreview] = useState<PreviewFile | null>(null);
  const subColor = useColorModeValue('gray.600', 'whiteAlpha.700');
  const border = useColorModeValue('gray.200', 'whiteAlpha.300');

  const doUpload = async (f?: File) => {
    if (!f) return;
    try {
      await upload(f);
      toast({ status: 'success', title: `${k.noun.charAt(0).toUpperCase()}${k.noun.slice(1)} uploaded`, description: `${f.name} is filed on this document (and its deal chain).` });
    } catch (e: any) {
      const msg = e?.response?.data;
      toast({
        status: 'error',
        title: `The ${k.noun} did NOT upload`,
        description: typeof msg === 'string' && msg ? msg : 'Please try again — if it keeps failing, send a screenshot of the console.',
        duration: 12000,
        isClosable: true,
      });
    }
  };

  const doRemove = async (id: string) => {
    try {
      await remove(id);
      toast({ status: 'success', title: `${k.noun.charAt(0).toUpperCase()}${k.noun.slice(1)} removed.` });
    } catch (e: any) {
      toast({ status: 'error', title: `Could not remove the ${k.noun}`, description: String(e?.response?.data ?? '') });
    }
  };

  return (
    <Box borderWidth="1px" borderColor={border} borderRadius="md" p={3} mt={mt}>
      <Flex align="center" mb={2}>
        <Text fontSize="sm" fontWeight="semibold">{k.title}</Text>
        <Text fontSize="xs" color={subColor} ml={2}>{k.blurb}</Text>
      </Flex>
      <HStack spacing={2} wrap="wrap">
        {(files ?? []).map((f) => (
          <Badge
            key={f.id}
            variant="subtle"
            colorScheme="green"
            cursor="pointer"
            onClick={() => setPreview({ id: f.id, name: f.name, contentType: f.contentType })}
          >
            <HStack spacing={1}>
              <Text fontSize="xs">{f.name}</Text>
              <Box
                as="span"
                aria-label="Download"
                title="Download"
                onClick={(e) => { e.stopPropagation(); downloadStoredFile(f.id, f.name); }}
              >
                <FiDownload size={11} />
              </Box>
              <Box
                as="span"
                aria-label="Remove"
                title="Remove"
                onClick={(e) => { e.stopPropagation(); doRemove(f.id); }}
              >
                <FiTrash2 size={11} />
              </Box>
            </HStack>
          </Badge>
        ))}
        {(files?.length ?? 0) === 0 && <Text fontSize="xs" color={subColor}>None uploaded.</Text>}
        <input
          ref={fileRef}
          type="file"
          style={{ display: 'none' }}
          onChange={(e) => { doUpload(e.target.files?.[0]); e.target.value = ''; }}
        />
        <Button
          type="button"
          size="xs"
          variant="outline"
          leftIcon={<FiPaperclip />}
          isLoading={isUploading}
          onClick={() => fileRef.current?.click()}
        >
          Upload
        </Button>
      </HStack>
      <StoredFilePreviewModal file={preview} onClose={() => setPreview(null)} />
    </Box>
  );
};

ReceivedPoAttachments.defaultProps = { kind: 'po', mt: 3 };

export default ReceivedPoAttachments;
